import styled from 'styled-components'
import { ReactComponent as Chevron } from './chevron.svg'

interface ChevronIconProps {
  direction?: 'up' | 'down' | 'left' | 'right'
  size?: 'sm' | 'md' | 'lg'
  color?: string
  containerStyle?: React.CSSProperties
  onClick?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLSpanElement>) => void
  disableHover?: boolean
  disabled?: boolean
  ariaLabel?: string
}

const StyledChevron = styled(Chevron)<ChevronIconProps>`
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  color: ${(props) => props.color || props.theme.colors.text.primary};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: transform 0.25s ease-in-out;

  width: ${(props) => {
    switch (props.size) {
      case 'sm':
        return '12px'
      case 'md':
        return '18px'
      case 'lg':
        return '24px'
      default:
        return '18px'
    }
  }};

  height: ${(props) => {
    switch (props.size) {
      case 'sm':
        return '12px'
      case 'md':
        return '18px'
      case 'lg':
        return '24px'
      default:
        return '18px'
    }
  }};

  transform: ${(props) => {
    switch (props.direction) {
      case 'up':
        return 'rotate(90deg)'
      case 'down':
        return 'rotate(-90deg)'
      case 'left':
        return 'rotate(0deg)'
      case 'right':
        return 'rotate(180deg)'
      default:
        return 'rotate(0deg)'
    }
  }};
`

const IconContainer = styled.span<{ $disableHover?: boolean }>`
  padding: ${(props) => (props.$disableHover ? '0' : '5px')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease-in-out;

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(props) => props.theme.colors.border.primary};
    border-radius: 6px;
  }

  ${(props) =>
    !props.$disableHover &&
    `
    &:hover {
      border-radius: 100px;
      background-color: ${props.theme.colors.surface.muted};
    }
  `}
`

const ChevronIcon = ({
  direction,
  size,
  color,
  onClick,
  onKeyDown,
  containerStyle,
  disableHover,
  ariaLabel,
  disabled
}: ChevronIconProps) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (onKeyDown) onKeyDown(e)

    // Accessibility: trigger click on Enter/Space
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <IconContainer
      style={containerStyle}
      $disableHover={disableHover}
      role='button'
      tabIndex={0}
      aria-label='Toggle'
    >
      <StyledChevron
        direction={direction}
        size={size}
        color={color}
        disabled={disabled}
        aria-label={ariaLabel || 'Chevron Icon'}
        onKeyDown={disabled ? undefined : handleKeyDown}
        onClick={disabled ? undefined : onClick}
      />
    </IconContainer>
  )
}

export default ChevronIcon
